import { inject, Injectable, NgZone } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import Uppy from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';

import {
    ALLOWED_FILE_TYPES,
    MAX_FILE_SIZE_IN_BYTES,
    MAX_NUMBER_OF_FILES,
} from 'AgentApp/ts/constants/agent-file.constants';
import { AgentChatSession } from 'AgentApp/ts/interfaces/agent-chat-session.interface';
import { AgentEvent } from 'AgentApp/ts/interfaces/agent-event';
import { AgentFileMessageContent } from 'AgentApp/ts/interfaces/agent-file-message-content';
import { AgentProvider, DEFAULT_PROVIDER } from 'AgentApp/ts/interfaces/agent-provider.interface';
import { ClientContext } from 'AgentApp/ts/interfaces/client-context';
import { ClientToolResponse } from 'AgentApp/ts/interfaces/client-tool';
import { UpdateReportBuilderParams } from 'AgentApp/ts/interfaces/update-report-builder-tool.interfaces';

import { AgentSessionStateService } from 'AgentApp/ts/services/agent-session-state.service';
import { AjaxService } from 'Angular/services/core/ajax.service';
import { UrlsService } from 'Angular/services/core/urls.service';

import { buildAttachmentContextPrompt } from 'AgentApp/ts/utils/context-block';

import { PageContextClientTool } from 'AgentApp/ts/client-tools/page-context.client-tool';
import { UpdateReportBuilderAppStateClientTool } from 'AgentApp/ts/client-tools/update-report-builder-app-state.client-tool';

const UNKNOWN_ERROR_MESSAGE = 'An unexpected error occurred. Please reach out to [support](/contact_us) for help.';

// A passive listener stays open on an idle session so runs the workflow starts on its own (system
// notifications) render without a user message. It is reopened after each run ends and retried with
// backoff when the connection drops.
export const LISTEN_RESTART_DELAY_MS = 1000;
export const LISTEN_RETRY_BASE_DELAY_MS = 5000;
export const LISTEN_MAX_RETRIES = 5;

type StreamMode = 'run' | 'reconnect' | 'listen';

const LISTENER_REJECTION_EVENTS: ReadonlySet<string> = new Set(['error', 'error_max_sessions', 'error_usage_limit']);

const SSE_EVENT_TYPES = [
    'text_delta',
    'thinking_delta',
    'tool_call_start',
    'tool_call_pending_confirmation',
    'tool_call_stop',
    'done',
    'error',
    'error_max_sessions',
    'error_usage_limit',
    'usage_limit_warning',
    'requesting_confirmation',
    'interrupted',
    'interaction_id',
    'client_tool_request',
    'system_event_run_start',
    'session_ended',
] as const;

@Injectable({ providedIn: 'root' })
export class AgentTransportService {
    private readonly _ngZone = inject(NgZone);

    private readonly _urlsService = inject(UrlsService);

    private readonly _ajaxService = inject(AjaxService);

    private readonly _sessionState = inject(AgentSessionStateService);

    private readonly _pageContextClientTool = inject(PageContextClientTool);

    private readonly _updateReportBuilderStateClientTool = inject(UpdateReportBuilderAppStateClientTool);

    private readonly _eventSources = new Map<number, EventSource>();

    private readonly _listeningSessions = new Set<number>();

    private readonly _listenTimeouts = new Map<number, ReturnType<typeof setTimeout>>();

    private readonly _listenRetryCounts = new Map<number, number>();

    private readonly _interactionIds = new Map<number, string>();

    private readonly _lastClientContext = new Map<number, ClientContext>();

    private readonly _sessionProviders = new Map<number, AgentProvider>();

    private readonly _uppy: Uppy;

    private readonly _pendingConfirmations = new Map<
        number,
        { tool_use_id: string; answer: string; reason: string | null }[]
    >();

    private _isFileUploading = false;

    constructor() {
        this._uppy = new Uppy({
            autoProceed: false,
            restrictions: {
                maxFileSize: MAX_FILE_SIZE_IN_BYTES,
                maxNumberOfFiles: MAX_NUMBER_OF_FILES,
                allowedFileTypes: [...ALLOWED_FILE_TYPES],
            },
        });
        this.initUppy(DEFAULT_PROVIDER);
    }

    get isFileUploading(): boolean {
        return this._isFileUploading;
    }

    initUppy(provider: AgentProvider): void {
        const existing = this._uppy.getPlugin('XHRUpload');
        if (existing) {
            existing.setOptions({
                endpoint: this._urlsService.getUrl(this._urlsService.AGENT.FILE_UPLOAD, { provider }),
            });
        } else {
            this._uppy.use(XHRUpload, {
                endpoint: this._urlsService.getUrl(this._urlsService.AGENT.FILE_UPLOAD, { provider }),
                formData: true,
                fieldName: 'files',
                bundle: true,
            });
        }
    }

    async createSession(
        message: string,
        files: File[] = [],
        options: { agentPromptId?: number; name?: string; provider?: AgentProvider; model?: string } = {}
    ): Promise<{ id: number; name: string; provider: AgentProvider; model: string | null }> {
        const messageFiles = files.map((file) => ({
            attachment_url: file.name,
            attachment_mime_type: file.type,
            external_file_id: '',
        }));
        // Background invocations (e.g., scheduled task runs) provide a name and bypass the
        // pending-message UI signal — the user isn't waiting at the chat input.
        const isBackground = options.agentPromptId !== undefined;
        if (!isBackground) {
            this._sessionState.pendingNewSessionMessage.set({ text: message, files: messageFiles });
        }

        try {
            const url = this._urlsService.getUrl(this._urlsService.AGENT.SESSIONS);
            const body: {
                message: string;
                agent_prompt_id?: number;
                name?: string;
                provider?: AgentProvider;
                model?: string;
            } = {
                message,
            };
            if (options.agentPromptId !== undefined) {
                body.agent_prompt_id = options.agentPromptId;
            }
            if (options.name !== undefined) {
                body.name = options.name;
            }
            body.provider = options.provider ?? DEFAULT_PROVIDER;
            if (options.model !== undefined) {
                body.model = options.model;
            }
            const response = await firstValueFrom(
                this._ajaxService.postJsonDataObserveBody<{
                    agent_session_id: number;
                    name: string;
                    provider: AgentProvider;
                    model: string | null;
                }>(url, body)
            );
            this._sessionProviders.set(response.agent_session_id, response.provider);
            return {
                id: response.agent_session_id,
                name: response.name,
                provider: response.provider,
                model: response.model,
            };
        } catch (err) {
            await this._redirectIfSignedOut();
            throw err;
        } finally {
            if (!isBackground) {
                this._sessionState.pendingNewSessionMessage.set(null);
            }
        }
    }

    /**
     * Generate a smart display name for a session from its first message. This runs off the
     * critical path — the session is created with a placeholder name first so the agent run can
     * start immediately — so failures are swallowed and the placeholder is left in place.
     */
    async generateSessionDisplay(message: string): Promise<{ name: string } | null> {
        if (!message) {
            return null;
        }
        try {
            const url = this._urlsService.getUrl(this._urlsService.AGENT.SESSION_DISPLAY);
            return await firstValueFrom(this._ajaxService.postJsonDataObserveBody<{ name: string }>(url, { message }));
        } catch {
            return null;
        }
    }

    generateAndApplySessionDisplay(session: AgentChatSession, message: string): void {
        this.generateSessionDisplay(message).then((display) => {
            if (display) {
                this._sessionState.updateSession({ ...session, name: display.name });
            }
        });
    }

    async getSession(
        agentSessionId: number
    ): Promise<{ id: number; name: string; provider: AgentProvider; model: string | null }> {
        const url = this._urlsService.getUrl(this._urlsService.AGENT.SESSION_DETAIL, { agentSessionId });
        const response = await firstValueFrom(
            this._ajaxService.getDataObserveBody<{
                session: { id: number; name: string; provider: AgentProvider; model: string | null };
            }>(url)
        );
        this._sessionProviders.set(response.session.id, response.session.provider);
        return response.session;
    }

    private async _getSessionProvider(sessionId: number): Promise<AgentProvider> {
        const cached = this._sessionProviders.get(sessionId);
        if (cached) {
            return cached;
        }
        const session = await this.getSession(sessionId);
        return session.provider;
    }

    async getActiveSessions(userId: number, agentSessionId?: number): Promise<Record<string, string>> {
        let url = `${this._getBaseUrl()}/agent/active_sessions?user_id=${userId}`;
        if (agentSessionId !== undefined) {
            url += `&agent_session_id=${agentSessionId}`;
        }
        try {
            const response = await fetch(url, { credentials: 'include' });
            const data = await response.json();
            return data.active_sessions ?? {};
        } catch {
            return {};
        }
    }

    hasActiveConnection(sessionId: number): boolean {
        const es = this._eventSources.get(sessionId);
        return !!es && es.readyState !== EventSource.CLOSED;
    }

    setInteractionId(sessionId: number, interactionId: string): void {
        this._interactionIds.set(sessionId, interactionId);
    }

    async reconnectToSession(
        sessionId: number,
        userId: number,
        interactionId: string,
        lastMessageStreamId: string,
        clientContext?: ClientContext
    ): Promise<void> {
        this._closeEventSource(sessionId);
        this._sessionState.markReconnecting(sessionId);

        const params = new URLSearchParams({
            agent_session_id: sessionId.toString(),
            user_id: userId.toString(),
            interaction_id: interactionId,
        });
        if (lastMessageStreamId) {
            params.set('last_message_stream_id', lastMessageStreamId);
        }
        if (clientContext) {
            params.set('client_context', JSON.stringify(clientContext));
        }
        params.set('provider', await this._getSessionProvider(sessionId));

        const sseUrl = `${this._getBaseUrl()}/agent/new?${params.toString()}`;
        this._openEventSource(sessionId, sseUrl, 'reconnect');
    }

    async sendUserMessage(
        sessionId: number,
        text: string,
        files: File[] = [],
        clientContext: ClientContext | undefined = undefined,
        smartCompareSecurityIds: string[] | undefined = undefined
    ): Promise<void> {
        const messageFiles = files.map((file) => ({
            attachment_url: file.name,
            attachment_mime_type: file.type,
            external_file_id: '',
        }));
        this._sessionState.addUserMessage(sessionId, text, messageFiles);

        this._sessionState.markStreaming(sessionId);

        let provider: AgentProvider;
        try {
            provider = await this._getSessionProvider(sessionId);
        } catch {
            await this._failWithError(sessionId, UNKNOWN_ERROR_MESSAGE);
            return;
        }

        let fileContentBlocks: AgentFileMessageContent[] = [];
        if (files.length) {
            if (files.length > MAX_NUMBER_OF_FILES) {
                this._sessionState.handleEvent(sessionId, {
                    type: 'error',
                    data: `You can only upload up to ${MAX_NUMBER_OF_FILES} files at a time.`,
                });
                return;
            }
            this._uppy.getPlugin('XHRUpload')!.setOptions({
                endpoint: this._urlsService.getUrl(this._urlsService.AGENT.FILE_UPLOAD, { provider }),
            });
            this._uppy.setMeta({ session_id: sessionId.toString() });
            this._isFileUploading = true;
            try {
                files.forEach((file) => {
                    this._uppy.addFile({
                        name: file.name,
                        type: file.type,
                        data: file,
                    });
                });
            } catch {
                this._uppy.cancelAll();
                this._isFileUploading = false;
                const oversized = files.find((f) => f.size > MAX_FILE_SIZE_IN_BYTES);
                const errorData = oversized
                    ? `${oversized.name} exceeds the maximum file size of 32MB.`
                    : 'The file format is not supported. Supported file types include: .txt, .xlsx, .csv .jpeg, .png, .pdf.';
                this._sessionState.handleEvent(sessionId, { type: 'error', data: errorData });
                return;
            }
            let uploadErrorStatus: number | undefined;
            const onUploadError = (
                _file: unknown,
                _error: unknown,
                response: { status?: number } | undefined
            ): void => {
                if (uploadErrorStatus === undefined && response?.status) {
                    uploadErrorStatus = response.status;
                }
            };
            this._uppy.on('upload-error', onUploadError);

            try {
                const result = await this._uppy.upload().catch(() => undefined);
                if (uploadErrorStatus === 415) {
                    this._sessionState.handleEvent(sessionId, {
                        type: 'error',
                        data: 'The file format is not supported. Supported file types include: .txt, .xlsx, .csv .jpeg, .png, .pdf.',
                    });
                    return;
                }
                if (uploadErrorStatus === 403) {
                    // The organization disabled uploads after this tab loaded, so retrying won't help.
                    await this._failWithError(sessionId, 'File upload is disabled for your organization.');
                    return;
                }
                if (uploadErrorStatus !== undefined) {
                    this._sessionState.handleEvent(sessionId, {
                        type: 'error',
                        data: 'There was an error uploading the file. Try again or contact support if the error persists.',
                    });
                    return;
                }
                fileContentBlocks = (result?.successful?.[0]?.response?.body?.files ?? []) as AgentFileMessageContent[];
            } finally {
                this._uppy.off('upload-error', onUploadError);
                this._uppy.cancelAll();
                this._isFileUploading = false;
            }
        }

        const messageText = buildAttachmentContextPrompt(fileContentBlocks, text);
        const messageData = [
            ...fileContentBlocks.map((f) => ({ type: f.type, file_id: f.file_id })),
            { type: 'text', text: messageText },
        ];
        const encodedMessage = JSON.stringify({ type: 'user_input', data: messageData });

        const interactionId = this._interactionIds.get(sessionId);
        const params = new URLSearchParams({
            agent_session_id: sessionId.toString(),
            message: encodedMessage,
            user_id: window.USER.userId.toString(),
        });
        if (interactionId) {
            params.set('interaction_id', interactionId);
        }
        if (clientContext) {
            params.set('client_context', JSON.stringify(clientContext));
            this._lastClientContext.set(sessionId, clientContext);
        }
        if (smartCompareSecurityIds?.length) {
            params.set('smart_compare_ids', smartCompareSecurityIds.join(','));
        }
        params.set('provider', provider);

        const sseUrl = `${this._getBaseUrl()}/agent/new?${params.toString()}`;

        // Close any existing stream (including a passive listener) for this session; the listener
        // is reopened once this run ends.
        this._trackListening(sessionId);
        this._closeEventSource(sessionId);

        this._openEventSource(sessionId, sseUrl);
    }

    async interrupt(sessionId: number): Promise<void> {
        const interactionId = this._interactionIds.get(sessionId);
        if (!interactionId) {
            return;
        }

        this._endStream(sessionId);

        try {
            const url = `${this._getBaseUrl()}/agent/stop?agent_session_id=${sessionId}&interaction_id=${interactionId}`;
            await firstValueFrom(this._ajaxService.postJsonDataObserveBody(url, {}, false, true));
        } catch {
            // Ignore errors
        }

        this._sessionState.handleEvent(sessionId, { type: 'interrupted' });
    }

    async checkAndReconnect(sessionId: number, userId: number): Promise<void> {
        if (this.hasActiveConnection(sessionId)) {
            return;
        }
        const activeSessions = await this.getActiveSessions(userId);
        const interactionId = activeSessions[sessionId.toString()];
        if (!interactionId) {
            return;
        }
        this.setInteractionId(sessionId, interactionId);

        const chatState = this._sessionState.getChatState(sessionId);
        if (chatState.toolCallsToConfirm.length) {
            return;
        }
        const lastStreamId = this._sessionState.getLastMessageStreamId(sessionId);
        const clientContext = this._lastClientContext.get(sessionId);
        await this.reconnectToSession(sessionId, userId, interactionId, lastStreamId, clientContext);
    }

    async loadAndResumeSession(sessionId: number, userId: number): Promise<void> {
        if (this.hasActiveConnection(sessionId)) {
            return;
        }
        const activeSessions = await this.getActiveSessions(userId);
        const interactionId = activeSessions[sessionId.toString()];
        const isActive = !!interactionId;

        const provider = await this._sessionState.loadMessages(sessionId, isActive);
        this._sessionProviders.set(sessionId, provider);

        if (!isActive) {
            this._sessionState.markIdle(sessionId);
            this.startListening(sessionId);
            return;
        }
        this.setInteractionId(sessionId, interactionId);
        this._trackListening(sessionId);

        const chatState = this._sessionState.getChatState(sessionId);
        if (chatState.toolCallsToConfirm.length) {
            return;
        }
        const lastStreamId = this._sessionState.getLastMessageStreamId(sessionId);
        const clientContext = this._lastClientContext.get(sessionId);
        await this.reconnectToSession(sessionId, userId, interactionId, lastStreamId, clientContext);
    }

    /**
     * Keep a passive listener open on the session while it is idle, and reopen it after every run,
     * so runs the workflow starts on its own are rendered. Stopped by `disconnect`.
     */
    startListening(sessionId: number): void {
        this._trackListening(sessionId);
        if (!this.hasActiveConnection(sessionId)) {
            this._openListener(sessionId);
        }
    }

    stopListening(sessionId: number): void {
        this._listeningSessions.delete(sessionId);
        this._listenRetryCounts.delete(sessionId);
        this._clearListenTimeout(sessionId);
    }

    isListening(sessionId: number): boolean {
        return this._listeningSessions.has(sessionId);
    }

    disconnect(sessionId: number): void {
        this.stopListening(sessionId);
        this._closeEventSource(sessionId);
        this._sessionState.markDisconnected(sessionId);
        this._lastClientContext.delete(sessionId);
        this._sessionProviders.delete(sessionId);
    }

    detach(sessionId: number): void {
        this.stopListening(sessionId);
        this._closeEventSource(sessionId);
    }

    confirmToolCall(
        sessionId: number,
        userId: number,
        authorize: boolean,
        alwaysApply: boolean,
        toolCall: { tool_use_id: string; name: string },
        reason: string | null = null
    ): void {
        let answer: string;
        if (authorize) {
            answer = alwaysApply ? 'allow_always' : 'allow';
        } else {
            answer = 'reject';
        }
        const confirmation = { tool_use_id: toolCall.tool_use_id, answer, reason };

        if (!this._pendingConfirmations.has(sessionId)) {
            this._pendingConfirmations.set(sessionId, []);
        }
        this._pendingConfirmations.get(sessionId)!.push(confirmation);

        this._sessionState.removeToolCallConfirmation(sessionId, toolCall.tool_use_id);

        const remaining = this._sessionState.getChatState(sessionId).toolCallsToConfirm;
        if (remaining.length === 0) {
            this._sendConfirmations(sessionId, userId);
        }
    }

    private _openEventSource(sessionId: number, url: string, mode: StreamMode = 'run'): void {
        const eventSource = new EventSource(url, { withCredentials: true });
        const isReconnection = mode === 'reconnect';
        const isListener = mode === 'listen';
        let receivedEvent = false;
        let reconnectionTimeout: ReturnType<typeof setTimeout> | null = null;

        if (isReconnection) {
            reconnectionTimeout = setTimeout(() => {
                if (!receivedEvent) {
                    this._endStream(sessionId);
                    this._sessionState.handleEvent(sessionId, { type: 'done', data: [] });
                }
            }, 5000);
        }

        SSE_EVENT_TYPES.forEach((eventType) => {
            eventSource.addEventListener(eventType, (e: Event) => {
                if (!(e instanceof MessageEvent)) {
                    return;
                }
                this._ngZone.run(() => {
                    if (isListener) {
                        if (!receivedEvent && LISTENER_REJECTION_EVENTS.has(eventType)) {
                            // The proxy refused the listener rather than a run failing: nothing to show,
                            // back off instead of reopening every second.
                            this._closeEventSource(sessionId);
                            this._retryListener(sessionId);
                            return;
                        }
                        if (eventType !== 'interaction_id') {
                            // The listener works: a later drop starts the backoff from scratch.
                            this._listenRetryCounts.delete(sessionId);
                        }
                    }
                    if (eventType !== 'interaction_id') {
                        if (isListener && !receivedEvent && eventType !== 'done' && eventType !== 'error') {
                            // A run started without a user message: reset the per-run state so its
                            // output renders as a new assistant message.
                            this._sessionState.markStreaming(sessionId);
                        }
                        receivedEvent = true;
                        if (reconnectionTimeout) {
                            clearTimeout(reconnectionTimeout);
                            reconnectionTimeout = null;
                        }
                    }
                    this._handleSSEEvent(sessionId, eventType, e.data);
                });
            });
        });

        eventSource.onerror = () => {
            if (isReconnection && !receivedEvent) {
                return;
            }
            // Errors from a stream we already closed or replaced are not failures.
            if (this._eventSources.get(sessionId) !== eventSource) {
                return;
            }
            this._ngZone.run(() => {
                this._closeEventSource(sessionId);
                if (reconnectionTimeout) {
                    clearTimeout(reconnectionTimeout);
                }
                if (isListener && !receivedEvent) {
                    // Nothing was in progress on a passive listener, so there is no error to show.
                    this._retryListener(sessionId);
                    return;
                }
                if (isListener) {
                    // A reply was streaming on the listener: end it like any lost run, then listen again.
                    this._scheduleListen(sessionId, LISTEN_RESTART_DELAY_MS);
                }
                this._failWithError(sessionId, 'Connection lost. Please try again.');
            });
        };

        this._eventSources.set(sessionId, eventSource);
    }

    private _openListener(sessionId: number): void {
        const params = new URLSearchParams({
            agent_session_id: sessionId.toString(),
            user_id: window.USER.userId.toString(),
            listen: 'true',
        });
        const interactionId = this._interactionIds.get(sessionId);
        if (interactionId) {
            params.set('interaction_id', interactionId);
        }
        const clientContext = this._lastClientContext.get(sessionId);
        if (clientContext) {
            params.set('client_context', JSON.stringify(clientContext));
        }
        this._openEventSource(sessionId, `${this._getBaseUrl()}/agent/new?${params.toString()}`, 'listen');
    }

    private _trackListening(sessionId: number): void {
        this._listeningSessions.add(sessionId);
        this._clearListenTimeout(sessionId);
    }

    private _scheduleListen(sessionId: number, delayMs: number): void {
        if (!this._listeningSessions.has(sessionId)) {
            return;
        }
        this._clearListenTimeout(sessionId);
        const timeout = setTimeout(() => {
            this._listenTimeouts.delete(sessionId);
            if (this._listeningSessions.has(sessionId) && !this.hasActiveConnection(sessionId)) {
                this._openListener(sessionId);
            }
        }, delayMs);
        this._listenTimeouts.set(sessionId, timeout);
    }

    private _retryListener(sessionId: number): void {
        const retries = this._listenRetryCounts.get(sessionId) ?? 0;
        if (retries >= LISTEN_MAX_RETRIES) {
            this.stopListening(sessionId);
            return;
        }
        this._listenRetryCounts.set(sessionId, retries + 1);
        this._scheduleListen(sessionId, LISTEN_RETRY_BASE_DELAY_MS * 2 ** retries);
    }

    private _clearListenTimeout(sessionId: number): void {
        const timeout = this._listenTimeouts.get(sessionId);
        if (timeout) {
            clearTimeout(timeout);
            this._listenTimeouts.delete(sessionId);
        }
    }

    /**
     * Close the stream for a run that ended and, if the session is being listened to, reopen the
     * passive listener shortly after.
     */
    private _endStream(sessionId: number): void {
        this._closeEventSource(sessionId);
        this._scheduleListen(sessionId, LISTEN_RESTART_DELAY_MS);
    }

    private _closeEventSource(sessionId: number): void {
        const existing = this._eventSources.get(sessionId);
        if (existing) {
            existing.close();
            this._eventSources.delete(sessionId);
        }
    }

    private _handleSSEEvent(sessionId: number, eventType: string, data: string): void {
        if (eventType === 'interaction_id') {
            this._interactionIds.set(sessionId, data);
            return;
        }

        if (eventType === 'done' || eventType === 'error') {
            this._endStream(sessionId);
        }

        if (eventType === 'session_ended') {
            // The workflow is gone (idle timeout, closed or failed): nothing more will arrive until the
            // user sends a message, which opens a fresh stream, so there is nothing to listen to.
            this.stopListening(sessionId);
            this._closeEventSource(sessionId);
            this._sessionState.handleEvent(sessionId, { type: 'done', data: [] });
            return;
        }

        if (eventType === 'error_max_sessions') {
            this._endStream(sessionId);
            this._sessionState.handleEvent(sessionId, {
                type: 'error',
                data: 'You have too many active sessions. Please wait for one to finish before starting a new one.',
            });
            return;
        }

        if (eventType === 'error_usage_limit') {
            this._endStream(sessionId);
            const decoded = data.replace(/%0A/g, '\n').replace(/%0D/g, '\r');
            this._sessionState.handleEvent(sessionId, { type: 'error', data: decoded });
            return;
        }

        if (eventType === 'usage_limit_warning') {
            const decoded = data.replace(/%0A/g, '\n').replace(/%0D/g, '\r');
            this._sessionState.handleEvent(sessionId, { type: 'usage_warning', data: decoded });
            return;
        }

        if (eventType === 'error') {
            this._sessionState.handleEvent(sessionId, { type: 'error', data: UNKNOWN_ERROR_MESSAGE });
            return;
        }

        if (eventType === 'client_tool_request') {
            try {
                const parsed = JSON.parse(data) as
                    | Array<{ tool_use_id: string; name: string; input: unknown }>
                    | {
                          type: string;
                          data: Array<{ tool_use_id: string; name: string; input: unknown }>;
                      };
                const requests = Array.isArray(parsed) ? parsed : parsed?.data;
                if (Array.isArray(requests)) {
                    this._handleClientToolRequest(sessionId, requests).catch(() => {
                        this._sessionState.handleEvent(sessionId, {
                            type: 'error',
                            data: 'Failed to execute client tool. Please try again.',
                        });
                    });
                }
            } catch {
                // ignore incorrect payloads
            }
            return;
        }

        try {
            const parsed: AgentEvent = JSON.parse(data);
            this._sessionState.handleEvent(sessionId, parsed);
        } catch {
            // Ignore non-JSON event
        }
    }

    private async _sendConfirmations(sessionId: number, userId: number): Promise<void> {
        const interactionId = this._interactionIds.get(sessionId);
        const confirmations = this._pendingConfirmations.get(sessionId) ?? [];
        this._pendingConfirmations.delete(sessionId);

        if (!interactionId || !confirmations.length) {
            return;
        }

        try {
            const params = new URLSearchParams({
                agent_session_id: sessionId.toString(),
                interaction_id: interactionId,
                user_id: userId.toString(),
            });
            const url = `${this._getBaseUrl()}/agent/confirm?${params.toString()}`;
            await firstValueFrom(this._ajaxService.postJsonDataObserveBody(url, { confirmations }, false, true));

            // Reconnect to the SSE stream to receive subsequent events
            if (!this.hasActiveConnection(sessionId)) {
                const lastStreamId = this._sessionState.getLastMessageStreamId(sessionId);
                const clientContext = this._lastClientContext.get(sessionId);
                await this.reconnectToSession(sessionId, userId, interactionId, lastStreamId, clientContext);
            }
        } catch {
            await this._failWithError(sessionId, 'Failed to send tool confirmation. Please try again.');
        }
    }

    private async _handleClientToolRequest(
        sessionId: number,
        requests: Array<{ tool_use_id: string; name: string; input: unknown }>
    ): Promise<void> {
        const responses = await Promise.all(
            requests.map((req) => this._executeClientTool(req.name, req.input, req.tool_use_id))
        );
        const sent = await this._sendClientToolResponses(sessionId, responses);
        const interactionId = this._interactionIds.get(sessionId);
        if (sent && interactionId && !this.hasActiveConnection(sessionId)) {
            const lastStreamId = this._sessionState.getLastMessageStreamId(sessionId);
            const clientContext = this._lastClientContext.get(sessionId);
            await this.reconnectToSession(sessionId, window.USER.userId, interactionId, lastStreamId, clientContext);
        }
    }

    private async _executeClientTool(name: string, input: unknown, toolUseId: string): Promise<ClientToolResponse> {
        try {
            switch (name) {
                case 'get_current_page_context':
                    return {
                        tool_use_id: toolUseId,
                        name,
                        result: await this._pageContextClientTool.execute(input),
                    };
                case 'update_report_builder_app_state':
                    return {
                        tool_use_id: toolUseId,
                        name,
                        result: await this._updateReportBuilderStateClientTool.execute(
                            input as UpdateReportBuilderParams
                        ),
                    };
                default:
                    return {
                        tool_use_id: toolUseId,
                        name,
                        error: { message: `Unknown client tool: ${name}` },
                    };
            }
        } catch (err) {
            return {
                tool_use_id: toolUseId,
                name,
                error: { message: err instanceof Error ? err.message : String(err) },
            };
        }
    }

    private async _sendClientToolResponses(sessionId: number, responses: ClientToolResponse[]): Promise<boolean> {
        const interactionId = this._interactionIds.get(sessionId);
        if (!interactionId) return false;
        try {
            const params = new URLSearchParams({
                agent_session_id: sessionId.toString(),
                interaction_id: interactionId,
                user_id: window.USER.userId.toString(),
            });
            const url = `${this._getBaseUrl()}/agent/client_tool_response?${params.toString()}`;
            await firstValueFrom(this._ajaxService.postJsonDataObserveBody(url, { responses }, false, true));
            return true;
        } catch {
            await this._failWithError(sessionId, 'Failed to send client tool response. Please try again.');
            return false;
        }
    }

    private async _failWithError(sessionId: number, message: string): Promise<void> {
        if (await this._redirectIfSignedOut()) {
            return;
        }
        this._sessionState.handleEvent(sessionId, { type: 'error', data: message });
    }

    // EventSource exposes no status code, so a failed request is checked against Django directly.
    private async _redirectIfSignedOut(): Promise<boolean> {
        try {
            const url = this._urlsService.getUrl(this._urlsService.ACCOUNTS.SESSION_VALIDATE);
            await firstValueFrom(this._ajaxService.getDataObserveBody(url));
            return false;
        } catch (err) {
            if ((err as { status?: number })?.status !== 403) {
                return false;
            }
        }
        this._redirectToLogin();
        return true;
    }

    private _redirectToLogin(): void {
        const next = window.location.pathname + window.location.search + window.location.hash;
        window.location.href = `/login?next=${encodeURIComponent(next)}`;
    }

    private _getBaseUrl(): string {
        return window.location.hostname === 'localhost'
            ? `${window.location.protocol}//localhost:3003`
            : process.env.AI_PROXY_BASE_URL!;
    }
}
